import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Input } from './input';
import { useReadOnly } from './read-only-context';

/*
 * Inputs that edit a local draft and commit on Enter or blur (Escape reverts). The store only sees
 * valid, final values — one command (and one undo step) per edit, not per keystroke.
 */

interface CommitState {
  draft: string;
  error: string | null;
  editing: boolean;
}

export interface CommitFieldProps<T> {
  value: T;
  /** Text shown when not editing, and the initial draft. */
  format: (value: T) => string;
  /** Returns the parsed value or an error message. */
  parse: (text: string) => { ok: true; value: T } | { ok: false; error: string };
  onCommit: (value: T) => void;
  id?: string | undefined;
  'aria-label'?: string;
  'aria-describedby'?: string | undefined;
  numeric?: boolean;
  /** Unit or symbol shown inside the field (e.g. "€", "%"). */
  adornment?: ReactNode;
  placeholder?: string;
  className?: string;
  /** Called with the current error so a parent <Field> can show it. */
  onErrorChange?: (error: string | null) => void;
}

export function CommitField<T>({
  value,
  format,
  parse,
  onCommit,
  numeric = false,
  adornment,
  className,
  onErrorChange,
  ...inputProps
}: CommitFieldProps<T>) {
  const readOnly = useReadOnly();
  const [state, setState] = useState<CommitState>({
    draft: format(value),
    error: null,
    editing: false,
  });

  // Follow external changes (undo, other edits) while not editing — adjusted during render.
  const formatted = format(value);
  const [shown, setShown] = useState(formatted);
  if (shown !== formatted) {
    setShown(formatted);
    if (!state.editing) setState({ draft: formatted, error: null, editing: false });
  }

  useEffect(() => {
    onErrorChange?.(state.error);
  }, [state.error, onErrorChange]);

  /** Set by Escape so the blur that follows doesn't commit the discarded draft. */
  const cancelled = useRef(false);

  const commit = (): void => {
    if (readOnly) return;
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    const result = parse(state.draft);
    if (!result.ok) {
      setState((s) => ({ ...s, error: result.error }));
      return;
    }
    setState({ draft: format(result.value), error: null, editing: false });
    if (format(result.value) !== formatted || result.value !== value) onCommit(result.value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      cancelled.current = true;
      setState({ draft: formatted, error: null, editing: false });
      event.currentTarget.blur();
    }
  };

  return (
    <div className={cn('relative', className)}>
      <Input
        {...inputProps}
        numeric={numeric}
        readOnly={readOnly}
        value={state.draft}
        aria-invalid={state.error !== null || undefined}
        autoComplete="off"
        spellCheck={false}
        className={adornment === undefined ? undefined : 'pr-7'}
        onFocus={(e) => {
          setState((s) => ({ ...s, editing: true }));
          // Select after focus settles (selecting inside the focus event fights some input stacks).
          const input = e.currentTarget;
          setTimeout(() => {
            if (document.activeElement === input) input.select();
          }, 0);
        }}
        onChange={(e) => {
          const draft = e.currentTarget.value;
          setState((s) => ({ ...s, draft, editing: true }));
        }}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
      {adornment !== undefined && (
        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-text-3">
          {adornment}
        </span>
      )}
    </div>
  );
}
